<script setup lang="ts">
  /**
   * Page header — 03-components/12d-page-header · OSCloud DS 1.1.27
   */
  withDefaults(
    defineProps<{
      /** The heading's words. Optional only so the `title` slot can supply them instead. */
      title?: string
      accent?: string
      lead?: string
    }>(),
    { title: '', accent: '', lead: '' },
  )
</script>

<template>
  <div class="page-header">
    <!-- The `title` slot is the `lead` slot's counterpart: a page whose heading is a record's
         own name - a ticket subject, a domain - renders it itself, because the words are data
         and may already be marked up. The prop stays the plain-string path. -->
    <h1 class="page-header__title">
      <slot name="title">{{ title }}</slot>
      <!-- An explicit word space. Vue condenses the whitespace between two elements written on
           separate lines, and the title became one such element when it gained its slot — without
           this the heading reads as "MyInvoices" to anything consuming its text. The flex gap is
           what spaces the two visually; this is what separates them as words. -->
      <template v-if="accent">{{ ' ' }}</template>
      <em v-if="accent" class="page-header__accent">{{ accent }}</em>
      <slot name="badge" />
    </h1>

    <!-- The slot carries a lead that is more than a sentence - a reference, a status
         badge, a date - without the page reaching past the block for its own paragraph. -->
    <p v-if="lead || $slots.lead" class="page-header__lead">
      <slot name="lead">{{ lead }}</slot>
    </p>

    <div v-if="$slots.actions" class="page-header__actions">
      <slot name="actions" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .page-header {
    --page-header-title-size: 34px; /* the card's declared exception to the type scale */
    --page-header-gap: 28px; /* the card's declared exception to --bf-sp-* */
    display: grid;
    grid-template-columns: 1fr auto;
    column-gap: var(--bf-sp-2);
    align-items: start;
    margin: 0 0 var(--page-header-gap);

    &__title {
      grid-column: 1;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--bf-sp-1);
      margin: 0;
      font-family: var(--bf-font-display);
      font-size: var(--page-header-title-size);
      font-weight: var(--bf-fw-extrabold);
      line-height: var(--bf-lh-tight);
      letter-spacing: var(--bf-tr-tight);
      color: var(--bf-deep-blue);
    }

    &__accent {
      font-style: italic;
      font-weight: var(--bf-fw-bold);
      color: var(--bf-orange-dark);
    }

    &__lead {
      grid-column: 1;
      max-width: 60ch;
      margin: var(--bf-sp-2) 0 0;
      font-family: var(--bf-font-body);
      font-size: var(--bf-fs-base);
      line-height: var(--bf-lh-normal);
      color: var(--bf-fg-2);
    }

    &__actions {
      grid-column: 2;
      grid-row: 1;
      display: flex;
      align-items: center;
      gap: var(--bf-sp-2);
      flex-wrap: wrap;
    }
  }

  @media (max-width: 600px) {
    /* --bf-bp-sm */
    .page-header {
      grid-template-columns: 1fr;
      row-gap: var(--bf-sp-2);
    }

    .page-header__title {
      /* No margin-bottom: the stacked gap is the grid's row-gap. A title margin lands between the
         title and the lead, on top of the lead's own margin-top (see BfPageHeader.md). */
      font-size: var(--bf-fs-xl);
    }

    .page-header__actions {
      grid-column: 1;
      grid-row: auto;
      width: 100%;
    }

    .page-header__actions :deep(.button) {
      flex: 1;
      justify-content: center;
    }
  }
</style>
